import { useState } from "react";
import { ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";
import { titleCase } from "@/lib/api";
import { cn } from "@/lib/utils";

const UNKNOWN = "Unknown";
const cap = (s) => (s ? titleCase(String(s).trim().toLowerCase()) : UNKNOWN);
const txt = (s) => (s && String(s).trim()) || UNKNOWN;
const yesNo = (b) => (b ? "Yes" : "No");
const ODO_BUCKETS = ["Under 25,000 km", "25,000–50,000 km", "50,000–1,00,000 km", "Over 1,00,000 km", UNKNOWN];
const odoBucket = (km) => (km == null ? UNKNOWN : km < 25000 ? ODO_BUCKETS[0] : km < 50000 ? ODO_BUCKETS[1] : km < 100000 ? ODO_BUCKETS[2] : ODO_BUCKETS[3]);
const FUEL_BUCKETS = ["Full (75–100%)", "Half (25–75%)", "Low (under 25%)", UNKNOWN];
const fuelBucket = (p) => (p == null ? UNKNOWN : p >= 75 ? FUEL_BUCKETS[0] : p >= 25 ? FUEL_BUCKETS[1] : FUEL_BUCKETS[2]);
const AGE_BUCKETS = ["Under 3 years", "3–5 years", "5–10 years", "Over 10 years", UNKNOWN];
const ageBucket = (year) => { if (!year) return UNKNOWN; const a = new Date().getFullYear() - Number(year); return a < 3 ? AGE_BUCKETS[0] : a < 5 ? AGE_BUCKETS[1] : a < 10 ? AGE_BUCKETS[2] : AGE_BUCKETS[3]; };
const COMPLIANCE_LABEL = { insurance: "Insurance", puc: "PUC", fitness: "Fitness", tax: "Road tax", permit: "Permit", warranty: "Warranty" };
const STATUS_LABEL = { ready_to_drive: "Ready to drive", under_maintenance: "Under maintenance", expiring_soon: "Expiring soon" };
const gaps = (v) => Object.entries(v.compliance?.by_type || {}).filter(([, s]) => ["expired", "expiring_soon", "missing"].includes(s)).map(([t, s]) => `${COMPLIANCE_LABEL[t] || titleCase(t)} ${STATUS_LABEL[s] || s}`.replace(/ (\w)/, (m) => m.toLowerCase()));
const missing = (v) => [["Profile image", !v.image_path], ["VIN", !v.vin], ["Engine no.", !v.engine_no], ["Odometer", v.current_odometer_km == null], ["Year", !v.year], ["Colour", !v.color], ["Location", !v.location], ["FASTag", !v.fastag_id]].filter(([, m]) => m).map(([l]) => l);

// order: "count" (default) | "alpha" | "year" | array of fixed labels
export const FACETS = [
  { key: "group", label: "Group", get: (v) => txt(v.group) === UNKNOWN ? "No group" : txt(v.group) },
  { key: "status", label: "Status", get: (v) => v.status },
  { key: "compliance", label: "Compliance", get: (v) => v.compliance?.overall, order: ["valid", "expiring_soon", "missing", "expired"] },
  { key: "gaps", label: "Compliance gaps", get: gaps, order: "alpha" },
  { key: "make", label: "Make", get: (v) => txt(v.make), order: "alpha" },
  { key: "model", label: "Model", get: (v) => txt(v.model), order: "alpha" },
  { key: "year", label: "Year", get: (v) => (v.year ? String(v.year) : UNKNOWN), order: "year" },
  { key: "age", label: "Vehicle age", get: (v) => ageBucket(v.year), order: AGE_BUCKETS },
  { key: "fuel_type", label: "Fuel", get: (v) => txt(v.fuel_type) },
  { key: "transmission", label: "Transmission", get: (v) => txt(v.transmission) },
  { key: "vehicle_type", label: "Body type", get: (v) => txt(v.vehicle_type) },
  { key: "color", label: "Colour", get: (v) => cap(v.color), order: "alpha" },
  { key: "location", label: "Current location", get: (v) => txt(v.location), order: "alpha" },
  { key: "ownership", label: "Ownership", get: (v) => txt(v.ownership), order: "alpha" },
  { key: "odometer", label: "Odometer", get: (v) => odoBucket(v.current_odometer_km), order: ODO_BUCKETS },
  { key: "fuel_level", label: "Fuel level", get: (v) => fuelBucket(v.fuel_level_pct), order: FUEL_BUCKETS },
  { key: "fastag", label: "FASTag", get: (v) => yesNo(v.fastag_id), order: ["Yes", "No"] },
  { key: "issues", label: "Open issues", get: (v) => yesNo(v.open_issues > 0), order: ["Yes", "No"] },
  { key: "missing", label: "Missing details", get: missing, order: "alpha" },
];
const pretty = (k, val) => STATUS_LABEL[val] || (k === "fuel_type" && val !== UNKNOWN ? val.toUpperCase() : k === "transmission" && val.length <= 3 ? val.toUpperCase() : ["status", "compliance", "transmission", "vehicle_type"].includes(k) ? titleCase(val) : val);

export const emptySelection = () => Object.fromEntries(FACETS.map((f) => [f.key, []]));
export const countSelected = (sel) => Object.values(sel).reduce((n, a) => n + a.length, 0);
const matches = (facet, v, selected) => { const val = facet.get(v); return Array.isArray(val) ? val.some((x) => selected.includes(x)) : selected.includes(val); };

export function applyFacets(rows, sel, attention) {
  return rows.filter((v) => FACETS.every((f) => !sel[f.key]?.length || matches(f, v, sel[f.key])) && (!attention || v.open_issues > 0 || ["expired", "expiring_soon"].includes(v.compliance.overall)));
}

function sortOptions(facet, entries) {
  const o = facet.order;
  const unknownLast = (a, b) => (a[0] === UNKNOWN) - (b[0] === UNKNOWN);
  if (Array.isArray(o)) return entries.sort((a, b) => o.indexOf(a[0]) - o.indexOf(b[0]));
  if (o === "year") return entries.sort((a, b) => unknownLast(a, b) || Number(b[0]) - Number(a[0]));
  if (o === "alpha") return entries.sort((a, b) => unknownLast(a, b) || String(a[0]).localeCompare(String(b[0])));
  return entries.sort((a, b) => unknownLast(a, b) || b[1] - a[1] || String(a[0]).localeCompare(String(b[0])));
}

function facetCounts(rows, sel, facet) {
  const base = applyFacets(rows, { ...sel, [facet.key]: [] }, false);
  const counts = {};
  base.forEach((v) => { const val = facet.get(v); (Array.isArray(val) ? val : [val]).forEach((k) => { counts[k] = (counts[k] || 0) + 1; }); });
  return sortOptions(facet, Object.entries(counts));
}

const LIMIT = 8;

function Facet({ facet, rows, sel, onToggle }) {
  const selected = sel[facet.key] || [];
  const [open, setOpen] = useState(selected.length > 0);
  const [term, setTerm] = useState("");
  const [more, setMore] = useState(false);
  const options = facetCounts(rows, sel, facet);
  if (options.length <= 1 && selected.length === 0) return null;
  const long = options.length > LIMIT;
  const filtered = term ? options.filter(([val]) => pretty(facet.key, val).toLowerCase().includes(term.toLowerCase())) : options;
  const shown = more || !long || term ? filtered : filtered.filter(([val], i) => i < LIMIT || selected.includes(val));
  const slug = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <div className="border-b border-border/70 py-3 last:border-0" data-testid={`facet-${facet.key}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between text-sm font-semibold" data-testid={`facet-toggle-${facet.key}`}>
        <span className="flex items-center">{facet.label}{selected.length > 0 && <span className="ml-2 rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground" data-testid={`facet-count-${facet.key}`}>{selected.length}</span>}</span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-2">
          {long && (
            <div className="relative mb-1.5">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={`Search ${facet.label.toLowerCase()}`} data-testid={`facet-search-${facet.key}`} className="h-8 w-full rounded-lg border border-border bg-background pl-8 pr-2 text-xs outline-none focus:border-primary" />
            </div>
          )}
          <ul className="space-y-1">
            {shown.map(([val, n]) => {
              const checked = selected.includes(val);
              return (
                <li key={val}>
                  <label className={cn("flex min-h-[36px] cursor-pointer items-center gap-2 rounded-lg px-1.5 text-sm hover:bg-muted/70", checked && "font-medium")}>
                    <input type="checkbox" checked={checked} onChange={() => onToggle(facet.key, val)} data-testid={`facet-option-${facet.key}-${slug(val)}`} className="h-4 w-4 shrink-0 rounded border-border accent-[#0066cc]" />
                    <span className="min-w-0 flex-1 truncate">{pretty(facet.key, val)}</span>
                    <span className="text-xs text-muted-foreground">{n}</span>
                  </label>
                </li>
              );
            })}
            {filtered.length === 0 && <li className="px-1.5 py-1 text-xs text-muted-foreground">No matches</li>}
          </ul>
          {long && !term && filtered.length > LIMIT && <button type="button" onClick={() => setMore(!more)} data-testid={`facet-more-${facet.key}`} className="mt-1 px-1.5 text-xs font-medium text-primary hover:underline">{more ? "Show less" : `Show all ${filtered.length}`}</button>}
        </div>
      )}
    </div>
  );
}

export function FilterPanel({ rows, sel, setSel, attention, setAttention, className }) {
  const toggle = (k, val) => setSel({ ...sel, [k]: (sel[k] || []).includes(val) ? sel[k].filter((x) => x !== val) : [...(sel[k] || []), val] });
  const total = countSelected(sel) + (attention ? 1 : 0);
  return (
    <aside className={cn("fv-card p-4", className)} data-testid="filter-panel">
      <div className="mb-1 flex items-center justify-between"><h2 className="flex items-center gap-2 text-base font-semibold"><SlidersHorizontal className="h-4 w-4" />Filters</h2>{total > 0 && <button type="button" onClick={() => { setSel(emptySelection()); setAttention(false); }} data-testid="vehicle-clear-filters" className="text-sm font-medium text-primary hover:underline">Clear all</button>}</div>
      <label className="flex min-h-[40px] cursor-pointer items-center gap-2 border-b border-border/70 pb-3 text-sm font-medium"><input type="checkbox" checked={attention} onChange={() => setAttention(!attention)} data-testid="vehicle-attention-filter" className="h-4 w-4 accent-[#0066cc]" />Needs attention<span className="ml-auto text-xs text-muted-foreground">{applyFacets(rows, sel, true).length}</span></label>
      {FACETS.map((f) => <Facet key={f.key} facet={f} rows={rows} sel={sel} onToggle={toggle} />)}
    </aside>
  );
}

export function ActiveChips({ sel, setSel, attention, setAttention }) {
  const chips = [];
  if (attention) chips.push({ label: "Needs attention", clear: () => setAttention(false) });
  FACETS.forEach((f) => (sel[f.key] || []).forEach((val) => chips.push({ label: `${f.label}: ${pretty(f.key, val)}`, clear: () => setSel({ ...sel, [f.key]: sel[f.key].filter((x) => x !== val) }) })));
  if (chips.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap gap-1.5" data-testid="active-filter-chips">
      {chips.map((c) => <button key={c.label} type="button" onClick={c.clear} data-testid={`chip-${c.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="flex h-8 items-center gap-1 rounded-full bg-primary/10 px-3 text-xs font-medium text-primary hover:bg-primary/20">{c.label}<X className="h-3 w-3" /></button>)}
    </div>
  );
}
